// 大项目案例展示组件
import React, { useState } from 'react';
import { ChevronRight, X, Building2, TrendingUp, Users, Star } from 'lucide-react';

// 大项目案例数据
const largeProjects = [
  {
    id: 1,
    projectName: '某大型制造集团智能资产管理平台',
    industry: '制造业',
    coreResult: '资产利用率提升45%，年度节省维护成本1200万',
    tags: ['智能盘点', '预测性维护', '资产全生命周期'],
    detailLink: '#',
    background: '该集团拥有超过50万固定资产，传统人工盘点方式效率低下，资产流失严重。',
    painPoints: '盘点周期长（每年2次）、资产状态不明、重复采购造成浪费',
    solution: '构建企业级资产图谱，部署AI盘点机器人，实现资产全生命周期数字化管理。',
    results: '盘点效率提升10倍，资产利用率提升45%，一年节省维护成本超1200万元',
    testimonial: '中海服帮我解决了多年困扰的资产管理难题，团队非常专业！',
    testimonialAuthor: '某制造集团CIO'
  },
  {
    id: 2,
    projectName: '某上市公司知识管理体系',
    industry: '金融科技',
    coreResult: '员工离职知识保留率从15%提升至95%',
    tags: ['知识图谱', '经验留存', '智能问答'],
    detailLink: '#',
    background: '该上市公司核心业务依赖员工经验，但人员流动导致大量隐性知识流失。',
    painPoints: '老员工离职带走核心经验、新员工培训周期长至6个月、业务经验无法传承',
    solution: '通过情景图谱技术，将离职员工的决策过程、工作文档、沟通记录转化为结构化知识资产。',
    results: '知识保留率从15%提升至95%，新员工培训周期缩短至1个月，查询准确率达92%',
    testimonial: '现在员工离职不再担心了，所有经验都能沉淀下来。',
    testimonialAuthor: '某金融科技公司CHO'
  },
  {
    id: 3,
    projectName: '某知名物流企业智能调度大脑',
    industry: '物流运输',
    coreResult: '配送效率提升30%，年节约燃油成本800万',
    tags: ['智能调度', '路径优化', '预测分析'],
    detailLink: '#',
    background: '该物流企业拥有上千辆配送车辆，传统调度依赖人工经验，效率低下。',
    painPoints: '车辆空驶率高、油耗成本高、配送时效难以保证、司机工作负荷不均',
    solution: '构建物流情景图谱，部署智能调度小模型，实现路径实时优化与预测性配送。',
    results: '配送效率提升30%，单车日均配送量提升25%，年节约燃油成本约800万元',
    testimonial: '中海服的技术让我们的调度效率发生了质的飞跃。',
    testimonialAuthor: '某物流公司运营总监'
  }
];

// 中小项目服务数据
const smallServices = [
  {
    id: 1,
    name: 'AI智能盘点',
    icon: '📦',
    scenarios: ['年度资产盘点', '仓库库存核查', '设备巡检'],
    benefits: '效率提升10倍，节省80%人力成本'
  },
  {
    id: 2,
    name: '合同智能审核',
    icon: '📄',
    scenarios: ['合同风险识别', '条款自动提取', '合规性检查'],
    benefits: '审核时间缩短70%，风险识别率提升至95%'
  },
  {
    id: 3,
    name: '智能客服助手',
    icon: '💬',
    scenarios: ['客户咨询应答', '问题自动分类', '工单智能派发'],
    benefits: '客服效率提升50%，客户满意度提升至90%'
  },
  {
    id: 4,
    name: '报表自动生成',
    icon: '📊',
    scenarios: ['月度经营报表', '数据可视化', '趋势分析预测'],
    benefits: '报表时间从3天缩短至10分钟'
  },
  {
    id: 5,
    name: '会议智能纪要',
    icon: '🎙️',
    scenarios: ['会议记录', '待办事项提取', '任务自动派发'],
    benefits: '会议效率提升40%，跟进落实率提升至85%'
  },
  {
    id: 6,
    name: '员工经验沉淀',
    icon: '🧠',
    scenarios: ['新员工培训', '最佳实践萃取', '知识库构建'],
    benefits: '培训周期缩短50%，知识查找效率提升10倍'
  }
];

// 案例详情弹窗组件
const CaseModal = ({ project, onClose }) => {
  if (!project) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-[#0b101d] border border-slate-700 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white">
          <X className="w-6 h-6" />
        </button>
        
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-blue-400" />
          <span className="text-sm text-blue-400 font-medium">{project.industry}</span>
        </div>
        
        <h3 className="text-2xl font-bold text-white mb-6">{project.projectName}</h3>
        
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">项目背景</h4>
            <p className="text-slate-300">{project.background}</p>
          </div>
          
          <div>
            <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">客户痛点</h4>
            <p className="text-slate-300">{project.painPoints}</p>
          </div>
          
          <div>
            <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">解决方案</h4>
            <p className="text-slate-300">{project.solution}</p>
          </div>
          
          <div className="bg-blue-900/20 border border-blue-500/30 rounded-2xl p-4">
            <h4 className="text-sm font-bold text-blue-400 uppercase tracking-wider mb-2">实施成果</h4>
            <p className="text-white font-semibold">{project.results}</p>
          </div>
          
          <div>
            <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">客户评价</h4>
            <div className="flex items-start gap-3">
              <Star className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-slate-300 italic">"{project.testimonial}"</p>
            </div>
            <p className="text-slate-500 text-sm mt-2">— {project.testimonialAuthor}</p>
          </div>
        </div>
        
        <div className="mt-8 pt-6 border-t border-slate-700">
          <button className="w-full py-3 bg-white text-[#070b14] font-bold rounded-full hover:bg-slate-200 transition-colors">
            预约免费咨询
          </button>
        </div>
      </div>
    </div>
  );
};

// 大项目案例卡片
const LargeProjectCard = ({ project, onClick }) => (
  <div 
    className="bg-[#0b101d] border border-slate-800 rounded-2xl p-6 cursor-pointer hover:border-blue-500/50 transition-all group"
    onClick={onClick}
  >
    <div className="flex items-start justify-between mb-4">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="w-4 h-4 text-blue-400" />
          <span className="text-sm text-blue-400">{project.industry}</span>
        </div>
        <h4 className="text-lg font-bold text-white group-hover:text-blue-300 transition-colors">{project.projectName}</h4>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-blue-400 transition-colors" />
    </div>
    
    <div className="flex flex-wrap gap-2 mb-4">
      {project.tags.map((tag, idx) => (
        <span key={idx} className="px-3 py-1 text-xs bg-slate-800 text-slate-400 rounded-full">
          {tag}
        </span>
      ))}
    </div>
    
    <div className="flex items-center gap-2 text-emerald-400">
      <TrendingUp className="w-4 h-4" />
      <span className="text-sm font-medium">{project.coreResult}</span>
    </div>
  </div>
);

// 中小项目服务卡片
const SmallServiceCard = ({ service }) => (
  <div className="bg-[#0b101d] border border-slate-800 rounded-2xl p-6 hover:border-cyan-500/50 transition-all group">
    <div className="text-4xl mb-4">{service.icon}</div>
    <h4 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-300 transition-colors">{service.name}</h4>
    
    <div className="mb-4">
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">适用场景</p>
      <div className="flex flex-wrap gap-2">
        {service.scenarios.map((scenario, idx) => (
          <span key={idx} className="px-2 py-1 text-xs bg-cyan-900/20 text-cyan-400 rounded">
            {scenario}
          </span>
        ))}
      </div>
    </div>
    
    <div className="pt-4 border-t border-slate-800">
      <p className="text-sm text-emerald-400 font-medium">{service.benefits}</p>
    </div>
    
    <button className="w-full mt-4 py-2 border border-slate-600 text-slate-300 rounded-lg text-sm font-medium hover:bg-cyan-900/20 hover:border-cyan-500 hover:text-cyan-300 transition-all">
      立即咨询
    </button>
  </div>
);

// 业务分层展示主组件
export default function BusinessShowcase() {
  const [selectedCase, setSelectedCase] = useState(null);

  return (
    <section id="cases" className="py-24 bg-[#070b14]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 标题 */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">成功案例 · 轻量服务</h2>
          <p className="text-lg text-slate-400 max-w-3xl mx-auto">
            从大型企业数字化转型到中小企业降本增效，我们提供量身定制的解决方案
          </p>
        </div>

        {/* 大项目案例展示 */}
        <div className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-blue-900/30 flex items-center justify-center border border-blue-500/50">
              <Building2 className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">大型企业案例</h3>
              <p className="text-sm text-slate-500">已帮助多家上市公司完成数字化转型</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {largeProjects.map((project) => (
              <LargeProjectCard 
                key={project.id} 
                project={project} 
                onClick={() => setSelectedCase(project)}
              />
            ))}
          </div>
        </div>

        {/* 中小项目服务展示 */}
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-cyan-900/30 flex items-center justify-center border border-cyan-500/50">
              <Users className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">中小企业轻量化服务</h3>
              <p className="text-sm text-slate-500">主打降本增效，即刻部署，快速见效</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {smallServices.map((service) => (
              <SmallServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>

        {/* 案例详情弹窗 */}
        {selectedCase && (
          <CaseModal project={selectedCase} onClose={() => setSelectedCase(null)} />
        )}
      </div>
    </section>
  );
}

export { largeProjects, smallServices };